// Shared consulting-matrix logic for TeamPower scoring
// Used by both ResultsPage (individual) and AdminEventDetail (team-level)

export const DIMENSION_BANDS = {
  capability: [
    {
      range: [0, 45],
      level: "Critical Capability Gap",
      levelKey: "criticalCapabilityGap",
      teambuildingOutcomes: ["skillsFutureReadiness", "strategyClarityExecution", "accountabilityOwnership"],
      trainingFeatures: ["strategyExecution", "personalEffectivenessProductivity"]
    },
    {
      range: [46, 65],
      level: "Developing Capability",
      levelKey: "developingCapability",
      teambuildingOutcomes: ["strategyClarityExecution", "crossFunctionalCollaboration", "leadershipCapability"],
      trainingFeatures: ["leadershipPeopleManagement", "collaborationTeamDynamics"]
    },
    {
      range: [66, 80],
      level: "Functional Capability",
      levelKey: "functionalCapability",
      teambuildingOutcomes: ["meetingEfficiencyTimeUse", "accountabilityOwnership", "skillsFutureReadiness"],
      trainingFeatures: ["personalEffectivenessProductivity", "innovationFutureSkills"]
    },
    {
      range: [81, 100],
      level: "Advanced Capability",
      levelKey: "advancedCapability",
      teambuildingOutcomes: ["strategyClarityExecution", "talentRetentionGrowth", "leadershipCapability"],
      trainingFeatures: ["innovationFutureSkills", "leadershipPeopleManagement"]
    }
  ],
  collaboration: [
    {
      range: [0, 45],
      level: "Collaboration Breakdown",
      levelKey: "collaborationBreakdown",
      teambuildingOutcomes: ["crossFunctionalCollaboration", "communicationTransparency", "accountabilityOwnership"],
      trainingFeatures: ["collaborationTeamDynamics", "leadershipPeopleManagement"]
    },
    {
      range: [46, 65],
      level: "Misalignment / Silos",
      levelKey: "misalignmentSilos",
      teambuildingOutcomes: ["crossFunctionalCollaboration", "meetingEfficiencyTimeUse", "engagementMotivation"],
      trainingFeatures: ["collaborationTeamDynamics", "personalEffectivenessProductivity"]
    },
    {
      range: [66, 80],
      level: "Functional Collaboration",
      levelKey: "functionalCollaboration",
      teambuildingOutcomes: ["communicationTransparency", "accountabilityOwnership", "leadershipCapability"],
      trainingFeatures: ["leadershipPeopleManagement", "strategyExecution"]
    },
    {
      range: [81, 100],
      level: "High Trust Team",
      levelKey: "highTrustTeam",
      teambuildingOutcomes: ["talentRetentionGrowth", "strategyClarityExecution", "changeReadinessResilience"],
      trainingFeatures: ["innovationFutureSkills", "leadershipPeopleManagement"]
    }
  ],
  energy: [
    {
      range: [0, 45],
      level: "Disengagement / Burnout Risk",
      levelKey: "disengagementBurnoutRisk",
      teambuildingOutcomes: ["engagementMotivation", "changeReadinessResilience", "communicationTransparency"],
      trainingFeatures: ["changeResilience", "leadershipPeopleManagement"]
    },
    {
      range: [46, 65],
      level: "Low Momentum",
      levelKey: "lowMomentum",
      teambuildingOutcomes: ["engagementMotivation", "accountabilityOwnership", "meetingEfficiencyTimeUse"],
      trainingFeatures: ["personalEffectivenessProductivity", "strategyExecution"]
    },
    {
      range: [66, 80],
      level: "Stable Energy",
      levelKey: "stableEnergy",
      teambuildingOutcomes: ["talentRetentionGrowth", "leadershipCapability", "crossFunctionalCollaboration"],
      trainingFeatures: ["leadershipPeopleManagement", "innovationFutureSkills"]
    },
    {
      range: [81, 100],
      level: "High Drive Team",
      levelKey: "highDriveTeam",
      teambuildingOutcomes: ["strategyClarityExecution", "changeReadinessResilience", "skillsFutureReadiness"],
      trainingFeatures: ["innovationFutureSkills", "strategyExecution"]
    }
  ]
};

export const INTERVENTION_BANDS = [
  { range: [0, 45], intensity: "Foundation & Clarity Journey", intensityKey: "foundationClarityJourney", color: "red" },
  { range: [46, 60], intensity: "Alignment & Consistency Journey", intensityKey: "alignmentConsistencyJourney", color: "orange" },
  { range: [61, 75], intensity: "Acceleration & Execution Journey", intensityKey: "accelerationExecutionJourney", color: "yellow" },
  { range: [76, 88], intensity: "Growth & Leadership Journey", intensityKey: "growthLeadershipJourney", color: "green" },
  { range: [89, 100], intensity: "Strategic Impact & Innovation Journey", intensityKey: "strategicImpactInnovationJourney", color: "blue" }
];

export const RESET_INTERVENTION = {
  teambuildingOutcomes: ["strategyClarityExecution", "crossFunctionalCollaboration", "engagementMotivation"],
  trainingFeatures: ["leadershipPeopleManagement", "strategyExecution"]
};

export function getBand(dimension, score) {
  const bands = DIMENSION_BANDS[dimension];
  return bands.find(band => score >= band.range[0] && score <= band.range[1]) || bands[0];
}

export function getInterventionBand(score) {
  return INTERVENTION_BANDS.find(band => score >= band.range[0] && score <= band.range[1]) || INTERVENTION_BANDS[0];
}

export function getPriorityLever(capScore, colScore, enScore) {
  const scores = [
    { dimension: 'capability', score: capScore },
    { dimension: 'collaboration', score: colScore },
    { dimension: 'energy', score: enScore }
  ];
  scores.sort((a, b) => a.score - b.score);
  return scores;
}

export function checkResetCondition(capScore, colScore, enScore) {
  return [capScore, colScore, enScore].filter(s => s < 45).length >= 2;
}

export function getDevelopmentFocus(capScore, colScore, enScore) {
  if (checkResetCondition(capScore, colScore, enScore)) {
    return {
      isReset: true,
      teambuildingOutcomes: RESET_INTERVENTION.teambuildingOutcomes,
      trainingFeatures: RESET_INTERVENTION.trainingFeatures
    };
  }
  const priorityLever = getPriorityLever(capScore, colScore, enScore);
  const lowestDimension = priorityLever[0];
  const lowestBand = getBand(lowestDimension.dimension, lowestDimension.score);
  return {
    isReset: false,
    priorityDimension: lowestDimension.dimension,
    teambuildingOutcomes: lowestBand.teambuildingOutcomes,
    trainingFeatures: lowestBand.trainingFeatures
  };
}

export function getDimensionLevelKey(dimension, score) {
  const band = getBand(dimension, score);
  return band.levelKey;
}
